import React from 'react';
import LoadingSpinner from './LoadingSpinner';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost' | 'glass-primary' | 'glass-secondary' | 'glass-ghost';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  fullWidth?: boolean;
  customColor?: string;  // Override color for adventure styling
  children: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      fullWidth = false,
      customColor,
      disabled,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const baseClasses = 'inline-flex items-center justify-center font-medium rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed antialiased';
    
    const variantClasses = {
      primary: 'bg-slate-500 text-white hover:bg-slate-600 focus:ring-slate-400 active:bg-slate-700',
      secondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 active:bg-gray-400',
      danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 active:bg-red-800',
      ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-500 active:bg-gray-200',
      'glass-primary': 'text-white font-semibold shadow-lg hover:shadow-xl hover:shadow-2xl transition-all duration-300',
      'glass-secondary': 'text-gray-800 font-semibold shadow-lg hover:shadow-xl hover:shadow-2xl transition-all duration-300',
      'glass-ghost': 'text-slate-200 hover:text-white font-medium hover:shadow-lg transition-all duration-300'
    };
    
    const sizeClasses = {
      sm: 'px-3 py-2 text-sm min-h-[36px]',
      md: 'px-4 py-2.5 text-base min-h-[44px]',
      lg: 'px-6 py-3 text-lg min-h-[52px]'
    };
    
    const widthClasses = fullWidth ? 'w-full' : '';
    const cursorClasses = !disabled && !loading ? 'cursor-pointer' : '';
    
    const classes = [
      baseClasses,
      variantClasses[variant],
      sizeClasses[size],
      widthClasses,
      cursorClasses,
      className
    ].filter(Boolean).join(' ');

    // Glass effect styles for glass variants
    const getGlassStyles = () => {
      // If customColor is provided and variant is a glass type, use custom color
      if (customColor && variant.startsWith('glass')) {
        // Convert hex color to rgba with different opacities for gradient effect
        const getColorWithAlpha = (color: string, alpha: string) => {
          if (color.startsWith('#')) {
            return `${color}${alpha}`;
          }
          // For rgb/hsl, we'll use the color as-is with opacity in background
          return color;
        };
        
        return {
          background: `linear-gradient(135deg, ${getColorWithAlpha(customColor, '88')}, ${getColorWithAlpha(customColor, 'AA')})`,
          backdropFilter: 'blur(12px) saturate(150%)',
          WebkitBackdropFilter: 'blur(12px) saturate(150%)',
          border: `1px solid ${getColorWithAlpha(customColor, '40')}`,
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
          WebkitFontSmoothing: 'antialiased' as const,
          MozOsxFontSmoothing: 'grayscale' as const,
        };
      }
      
      // Default glass styles
      switch (variant) {
        case 'glass-primary':
          return {
            background: 'linear-gradient(135deg, rgba(100, 116, 139, 0.533), rgba(100, 116, 139, 0.667))', // Shadow Realm brighter slate grey
            backdropFilter: 'blur(12px) saturate(150%)',
            WebkitBackdropFilter: 'blur(12px) saturate(150%)',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            boxShadow: '0 8px 32px 0 rgba(30, 41, 59, 0.37)', // Darker shadow for contrast
            WebkitFontSmoothing: 'antialiased' as const,
            MozOsxFontSmoothing: 'grayscale' as const,
          };
        case 'glass-secondary':
          return {
            background: 'rgba(255, 255, 255, 0.12)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.35)',
            boxShadow: '0 8px 32px 0 rgba(255, 255, 255, 0.1)',
          };
        case 'glass-ghost':
          return {
            background: 'rgba(100, 116, 139, 0.06)', // Shadow Realm brighter slate grey
            backdropFilter: 'blur(16px) saturate(150%)',
            WebkitBackdropFilter: 'blur(16px) saturate(150%)',
            border: '1px solid rgba(100, 116, 139, 0.25)',
            boxShadow: '0 4px 16px 0 rgba(100, 116, 139, 0.1)',
          };
        default:
          return {};
      }
    };

    const glassStyles = getGlassStyles();

    // Ensure rounded corners and smooth transitions are always applied
    const combinedStyles = {
      ...glassStyles,
      borderRadius: '0.75rem', // 12px - matches rounded-xl
      transition: 'background 0.5s ease-in-out, border-color 0.5s ease-in-out, box-shadow 0.5s ease-in-out',
      ...props.style
    };

    return (
      <button
        ref={ref}
        className={classes}
        style={combinedStyles}
        disabled={disabled || loading}
        {...props}
      >
        {loading && (
          <LoadingSpinner 
            size="sm" 
            className="mr-2" 
          />
        )}
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
